import React from "react";
import { TYPE_META, DOMAIN_META, URGENCY_META, TYPE_BADGE_CLS, OWNER_META, VISIBILITY_META } from "../lib/meta";
import { User, Users, Lock, Check } from "lucide-react";

const base =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap";
const quiet =
  "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap text-[var(--ink5)]";

export function TypeBadge({ type, subtle }) {
  const m = TYPE_META[type];
  if (!m) return null;
  const Icon = m.icon;
  if (subtle) {
    return (
      <span className={quiet} data-testid={`badge-type-${type}`}>
        <Icon size={11} strokeWidth={1.6} />
        {m.label}
      </span>
    );
  }
  return (
    <span className={`${base} ${TYPE_BADGE_CLS}`} data-testid={`badge-type-${type}`}>
      <Icon size={12} strokeWidth={1.75} />
      {m.label}
    </span>
  );
}

export function DomainBadge({ domain, subtle }) {
  // Household cohort never sees a "Prospect" chip — display it as Work.
  const key = domain === "PROSPECT" ? "WORK" : domain;
  const m = DOMAIN_META[key];
  if (!m) return null;
  return (
    <span className={`${subtle ? quiet : `${base} ${m.cls}`}`} data-testid={`badge-domain-${key}`}>
      {m.label}
    </span>
  );
}

export function UrgencyBadge({ urgency }) {
  const m = URGENCY_META[urgency];
  if (!m) return null;
  return (
    <span className={`${base} ${m.cls}`} data-testid={`badge-urgency-${urgency}`}>
      {m.label}
    </span>
  );
}

export function OwnerBadge({ owner, name }) {
  const m = OWNER_META[owner];
  if (!m) return null;
  const Icon = owner === "me" ? User : Users;
  const label = owner === "partner" && name ? name : m.label;
  return (
    <span className={`${base} ${m.cls}`} data-testid={`badge-owner-${owner}`}>
      <Icon size={11} strokeWidth={1.9} />
      {label}
    </span>
  );
}

export function VisibilityBadge({ visibility, subtle }) {
  const m = VISIBILITY_META[visibility];
  if (!m) return null;
  const Icon = visibility === "shared" ? Users : Lock;
  if (subtle) {
    return (
      <span className={quiet} data-testid={`badge-visibility-${visibility}`}>
        <Icon size={11} strokeWidth={1.6} />
        {m.label}
      </span>
    );
  }
  return (
    <span
      className={`${base} ${
        visibility === "shared"
          ? "bg-[var(--pine-soft)] text-[var(--pine)] border-[var(--pine-line)]"
          : "bg-[var(--line2)] text-[var(--ink3)] border-[var(--line)]"
      }`}
      data-testid={`badge-visibility-${visibility}`}
    >
      <Icon size={11} strokeWidth={1.9} />
      {m.label}
    </span>
  );
}

export function StateBadge({ state }) {
  if (state !== "done") return null;
  return (
    <span
      className={`${base} bg-[var(--pine-soft)] text-[var(--pine)] border-[var(--pine-line)]`}
      data-testid="badge-state-done"
    >
      <Check size={11} strokeWidth={2.2} />
      Done
    </span>
  );
}

// Calm "In review" status pill (pine) for items still flagged needs_review.
export function ReviewBadge() {
  return (
    <span
      className={`${base} bg-[var(--pine-soft)] text-[var(--pine)] border-[var(--pine-line)]`}
      data-testid="badge-review"
    >
      In review
    </span>
  );
}

// Shown to the ASSIGNEE: a calm "New" marker on items just handed to them.
export function NewBadge() {
  return (
    <span
      className={`${base} bg-[var(--pine-soft)] text-[var(--pine)] border-[var(--pine-line)]`}
      data-testid="badge-new"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--pine)]" />
      New
    </span>
  );
}

